"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { cn } from "@/lib/utils"

const navItems = [
  { id: "hero", label: "Home" },
  { id: "services", label: "Services" },
  { id: "solutions", label: "Solutions" },
  { id: "research", label: "Research" },
  { id: "case-studies", label: "Case Studies" },
  { id: "company", label: "Company" },
  { href: "/careers", label: "Careers" },
  { href: "/contact", label: "Contact" },
]

export function SideNav() {
  const pathname = usePathname()
  const [activeSection, setActiveSection] = useState("hero")

  useEffect(() => {
    if (pathname !== "/") return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = (entry.target as HTMLElement).id
            if (sectionId) setActiveSection(sectionId)
          }
        })
      },
      { threshold: 0.3 },
    )

    navItems.forEach((item) => {
      if ("id" in item) {
        const element = document.getElementById(item.id)
        if (element) observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [pathname])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  // Remove the id sections that don't exist
  const validateNavItems = () => {
    const validIds = new Set(navItems.filter(item => "id" in item).map(item => item.id))
    const invalidIds = new Set(navItems.filter(item => !("href" in item) && !validIds.has(item.id as string)))
    return invalidIds
  }

  return (
    <nav className="fixed left-1/2 top-6 z-50 -translate-x-1/2 flex items-center justify-center" aria-label="Main navigation">
      <div className="flex flex-row items-center gap-3 rounded-full border border-border/30 bg-background/80 px-3 py-2 backdrop-blur-sm md:gap-5 md:px-8 md:py-3 overflow-x-auto max-w-[95vw] scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {navItems.map((item) => {
          if ("href" in item && item.href) {
            const isActive = pathname === item.href
            return (
              <Link
                key={item.href}
                href={item.href}
                className="group relative flex items-center gap-2"
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-300",
                    isActive ? "bg-accent scale-125" : "bg-muted-foreground/40 group-hover:bg-foreground/60",
                  )}
                />
                <span
                  className={cn(
                    "font-mono text-[8px] uppercase tracking-widest transition-colors duration-200 md:text-[11px]",
                    isActive ? "text-accent" : "text-muted-foreground group-hover:text-foreground/80",
                  )}
                >
                  {item.label}
                </span>
              </Link>
            )
          }
          const { id, label } = item
          const isScrollItemActive = pathname === "/" && activeSection === id
          const navItemClass = "group relative flex items-center gap-2"
          const dotClass = cn(
            "h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-300",
            isScrollItemActive ? "bg-accent scale-125" : "bg-muted-foreground/40 group-hover:bg-foreground/60",
          )
          const labelClass = cn(
            "font-mono text-[8px] uppercase tracking-widest transition-colors duration-200 md:text-[11px]",
            isScrollItemActive ? "text-accent" : "text-muted-foreground group-hover:text-foreground/80",
          )

          if (pathname === "/" && id) {
            return (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className={navItemClass}
                aria-label={`Go to ${label} section`}
              >
                <span className={dotClass} />
                <span className={labelClass}>{label}</span>
              </button>
            )
          }
          return (
            <Link key={id} href={`/#${id}`} className={navItemClass} aria-label={`Go to ${label} section`}>
              <span className={dotClass} />
              <span className={labelClass}>{label}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
